import { Component, computed, inject, input, output } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import {
    CardCatalogueDataSource,
    CardContainer,
    ChipsComponent,
    EMPTY_LINK,
    HeadingComponent,
    ICardComponent,
    IChip,
    ILink,
    ImageData,
    ImageItemComponent,
    LinkDirective,
    LinkService,
    MediaImage,
    SnowplowService,
    withConfigFallback,
} from '@sirius/common';
import { ICardEvent } from '../../../models';

@Component({
    selector: 'ems-card-event',
    templateUrl: './card-event.component.html',
    styleUrl: './card-event.component.scss',
    imports: [TranslatePipe, LinkDirective, ImageItemComponent, HeadingComponent, ChipsComponent],
    host: {
        // `is-light` keeps the white card readable on dark sections.
        class: 'ems-card-event sir-text-style-paragraph-s-default is-light',
    },
})
export class CardEventComponent implements ICardComponent<ICardEvent> {
    private static nextId = 0;

    private readonly snowplowService = inject(SnowplowService);
    private readonly linkService = inject(LinkService);

    readonly config = input<ICardEvent | undefined>(undefined);
    readonly imageData = input<ImageData | undefined>(undefined);
    readonly dataSource = input<CardCatalogueDataSource>('other');
    readonly container = input<CardContainer>('slider');

    readonly title = input('');
    readonly image = input<MediaImage | undefined>(undefined);
    readonly date = input<string | undefined>(undefined);
    readonly dateLabel = input('');
    readonly location = input('');
    readonly languages = input<string[]>([]);
    readonly attributes = input<IChip[]>([]);
    readonly tags = input<IChip[]>([]);
    readonly link = input<ILink>(EMPTY_LINK);

    readonly selectEvent = output<void>();

    protected readonly resolvedTitle = withConfigFallback(this.config, 'title', this.title);
    protected readonly resolvedImageData = withConfigFallback(this.config, 'imageData', this.imageData);
    protected readonly resolvedImage = withConfigFallback(this.config, 'image', this.image);
    protected readonly resolvedDate = withConfigFallback(this.config, 'date', this.date);
    protected readonly resolvedDateLabel = withConfigFallback(this.config, 'dateLabel', this.dateLabel);
    protected readonly resolvedLocation = withConfigFallback(this.config, 'location', this.location);
    protected readonly resolvedLanguages = withConfigFallback(this.config, 'languages', this.languages);
    protected readonly resolvedAttributes = withConfigFallback(this.config, 'attributes', this.attributes);
    protected readonly resolvedTags = withConfigFallback(this.config, 'tags', this.tags);
    protected readonly resolvedLink = withConfigFallback(this.config, 'link', this.link);

    protected readonly languageList = computed(() => this.resolvedLanguages().join(', '));

    private readonly idPrefix = `ems-card-event-${CardEventComponent.nextId++}`;
    protected readonly dateId = `${this.idPrefix}-date`;
    protected readonly locationId = `${this.idPrefix}-location`;

    protected readonly linkDescribedBy = computed(() => {
        const ids = [this.resolvedDateLabel() && this.dateId, this.resolvedLocation() && this.locationId];
        return ids.filter(Boolean).join(' ') || null;
    });

    protected onClick(): void {
        this.trackLinkClick();
        this.selectEvent.emit();
    }

    private trackLinkClick(): void {
        const link = this.resolvedLink();

        this.snowplowService.track('linkClick', {
            category: 'card',
            container: this.container(),
            identifier: link.tracking?.parameters['identifier'],
            target_url: this.linkService.getFullPath(link.href),
            outbound: link.target === '_blank',
            element_content: link.title,
            context: [],
        });
    }
}
